/**
 * driverHolidayTick — manages the 5-week annual holiday allowance for
 * each driver (created 11/06/2026 soir 9 in response to user feedback :
 * "5 semaines de congés par employé par année").
 *
 * Rules of the system :
 *   - Each driver has `holidayDaysRemaining` — 17/07/2026 (user prod) : le
 *     solde se constitue par CUMUL MENSUEL (+2,5 j au 1er de chaque mois,
 *     30 j/an, plafond 30 j). Les nouvelles recrues démarrent à 0 j.
 *   - On Jan 1, only the yearly counters (cumulés / pris) reset — the
 *     remaining balance carries over (still capped at 30 by the accrual).
 *   - When a driver is idle (no assigned vehicle on an active contract)
 *     for ≥ 2 consecutive days AND has at least 5 holiday days banked,
 *     they auto-take a 5-day vacation : `onHoliday: true`,
 *     `holidayEndDay = totalDays + 5`, `holidayDaysRemaining -= 5`.
 *   - During holidays they show as `status: 'holiday'` and cannot be
 *     assigned to a contract. The vehicle assignment is severed for the
 *     duration (and re-establishes when they return) — handled in
 *     `getDriverAvailability` checks elsewhere.
 *   - At `totalDays === holidayEndDay`, they return : `onHoliday: false`,
 *     `status: 'idle'`, `holidayEndDay = null`, `holidayStartDay = null`.
 *
 * Edge cases :
 *   - Drivers freshly hired start at 0 day and accrue monthly. Drivers
 *     taken over via M&A arrive with the days already accrued this year
 *     (proRatedHolidayAllowance).
 *   - Drivers already `on_break` (HR action) don't accrue holiday-time
 *     while paused (no need : they're not working).
 *   - Drivers in training do NOT trigger auto-holiday — finish training
 *     first.
 */


const HOLIDAY_FULL_YEAR = 25;        // 5 weeks × 5 working days (legacy const, kept for tests/UI)
const HOLIDAY_MONTHLY_ACCRUAL = 2.5; // 17/07/2026 — cumul mensuel (30 j/an, droit FR jours ouvrables)
const HOLIDAY_CARRYOVER_CAP = 30;    // Plafond de solde cumulable
const HOLIDAY_BLOCK_LENGTH = 5;      // Auto-vacation length when triggered
const HOLIDAY_TRIGGER_IDLE_DAYS = 2; // Idle days required before auto-trigger
const HOLIDAY_TRIGGER_MIN_BALANCE = 5; // Need at least 5 banked days to take a 5-day block

/**
 * Year-end reset : on Jan 1 (day === 1 && month === 1 of the new year),
 * top up each driver's holiday balance.
 *
 * @param {Array} drivers
 * @param {boolean} isYearStart — Jan 1 of a new game year
 * @param {number} [currentYear] — used to guarantee idempotency across ticks
 * @returns {Array} drivers with updated holiday balances
 *
 * 12/02/2026 (fix user prod « Mes chauffeurs récupèrent des congés en boucle,
 * ils ont eu 40 j de congés cette année ») — On stamp le chauffeur avec
 * `lastYearStartResetYear = currentYear`. Si le tick est rejoué (bug de
 * double-nextDay, save-load, tick de rollover mal ordonné), le reset ne
 * s'applique plus deux fois pour la même année → plus de solde qui remonte
 * mystérieusement.
 */
/**
 * 17/07/2026 — Refonte user prod : les chauffeurs CUMULENT désormais
 * +2,5 j/mois (30 j/an) au lieu de recevoir leur quota d'un coup.
 * Cette fonction ne sert plus qu'aux chauffeurs repris en M&A : ils
 * arrivent avec les jours déjà cumulés cette année chez leur ancien
 * employeur (janvier → 0 j, juillet → 15 j, décembre → 27,5 j).
 */
export function proRatedHolidayAllowance(month) {
  const m = Math.min(12, Math.max(1, Math.round(month || 1)));
  return Math.min(HOLIDAY_CARRYOVER_CAP, Math.round((m - 1) * HOLIDAY_MONTHLY_ACCRUAL * 10) / 10);
}

export function applyYearStartHolidayReset(drivers, isYearStart, currentYear = 0) {
  if (!isYearStart || !Array.isArray(drivers)) return drivers;
  return drivers.map(d => {
    // Idempotency guard : ne reset qu'une seule fois par année de jeu.
    if (currentYear > 0 && d.lastYearStartResetYear === currentYear) return d;
    // 17/07/2026 — plus de recharge +25 j : le solde se constitue par cumul
    // mensuel. Le 1er janvier remet uniquement à zéro les compteurs annuels
    // « cumulés / pris » (le solde restant est conservé, plafonné à 30 j).
    return {
      ...d,
      holidayDaysAccruedYear: 0,
      holidayDaysTakenYear: 0,
      lastYearStartResetYear: currentYear || d.lastYearStartResetYear || 0,
    };
  });
}

/**
 * 17/07/2026 — Cumul mensuel des congés (demande user prod : « les
 * chauffeurs ne devraient pas avoir les 25 jours tout de suite, ils en
 * cumulent 2,5 par mois »). Au 1er de chaque mois, chaque chauffeur gagne
 * +2,5 j, plafonné à HOLIDAY_CARRYOVER_CAP. Idempotent via
 * `lastHolidayAccrualTag` ("année-mois") pour résister aux ticks rejoués.
 */
export function applyMonthlyHolidayAccrual(drivers, isMonthStart, accrualTag) {
  if (!isMonthStart || !Array.isArray(drivers)) return drivers;
  return drivers.map(d => {
    if (accrualTag && d.lastHolidayAccrualTag === accrualTag) return d;
    return {
      ...d,
      holidayDaysRemaining: Math.min(
        HOLIDAY_CARRYOVER_CAP,
        Math.round(((d.holidayDaysRemaining || 0) + HOLIDAY_MONTHLY_ACCRUAL) * 10) / 10,
      ),
      holidayDaysAccruedYear: Math.round(((d.holidayDaysAccruedYear || 0) + HOLIDAY_MONTHLY_ACCRUAL) * 10) / 10,
      lastHolidayAccrualTag: accrualTag || d.lastHolidayAccrualTag || null,
    };
  });
}

/**
 * End-of-holiday : check if `totalDays >= holidayEndDay` for any driver
 * currently on holiday and bring them back online.
 *
 * 10/02/2026 (Lot L) — Retour user prod : « le chauffeur doit revenir
 * sur le contrat pour lequel il était attribué lors de son retour de
 * congés, malgré les corrections il revient disponible ». On regarde
 * désormais l'état de son véhicule au moment du retour : si le binding
 * `assignedVehicleId` pointe vers un véhicule en mission active, on
 * remet le chauffeur en `status: 'active'` (pas 'idle'). On retourne
 * aussi la liste des véhicules dont `interimAutoActivated` était vrai
 * pour que le caller puisse désactiver `useInterim` proprement.
 *
 * @returns {{
 *   drivers: Array,
 *   interimVehicleIdsToRelease: Array<string>,
 *   returningDrivers: Array<{ driverId, vehicleId, contractId }>
 * }}
 */
export function applyHolidayReturns(drivers, totalDays, vehicles = []) {
  if (!Array.isArray(drivers)) {
    return { drivers, interimVehicleIdsToRelease: [], returningDrivers: [] };
  }
  const vehiclesById = Object.fromEntries((vehicles || []).map(v => [v.id, v]));
  const interimVehicleIdsToRelease = [];
  const returningDrivers = [];
  const next = drivers.map(d => {
    if (d.onHoliday && d.holidayEndDay != null && totalDays >= d.holidayEndDay) {
      // Look up the vehicle the driver was assigned to (link preserved
      // during holidays — cf. tickVehicles healer exclusion).
      const linkedVehicle = d.assignedVehicleId ? vehiclesById[d.assignedVehicleId] : null;
      const vehicleStillActive = linkedVehicle
        && linkedVehicle.status === 'active'
        && !!linkedVehicle.assignedContractId;
      if (vehicleStillActive) {
        returningDrivers.push({
          driverId: d.id,
          vehicleId: linkedVehicle.id,
          contractId: linkedVehicle.assignedContractId,
        });
        // Release auto-interim if we set it at holiday start.
        if (linkedVehicle.interimAutoActivated) {
          interimVehicleIdsToRelease.push(linkedVehicle.id);
        }
      }
      return {
        ...d,
        onHoliday: false,
        holidayEndDay: null,
        holidayStartDay: null,
        // Return refreshed : −20% fatigue, +5 satisfaction.
        fatigue: Math.max(0, (d.fatigue || 0) - 20),
        satisfaction: Math.min(100, (d.satisfaction || 0) + 5),
        // Status logic : back to 'active' if vehicle still on a mission,
        // 'idle' otherwise.
        status: vehicleStillActive
          ? 'active'
          : (d.status === 'holiday' ? 'idle' : d.status),
      };
    }
    return d;
  });
  return { drivers: next, interimVehicleIdsToRelease, returningDrivers };
}

/**
 * Auto-trigger : pick drivers eligible for a 5-day vacation (idle, has
 * balance, not training, not on_break). To avoid mass simultaneous
 * absences, we only trigger AT MOST 1 driver per tick.
 *
 * Returns updated driver array + the id of the driver that just started
 * (or null if none).
 */
export function triggerAutoHoliday(drivers, totalDays, _vehicles) {
  if (!Array.isArray(drivers)) return { drivers, startedDriverId: null };
  // Find first eligible driver.
  const idx = drivers.findIndex(d => isDriverHolidayEligible(d));
  if (idx === -1) return { drivers, startedDriverId: null };
  const next = drivers.slice();
  const d = next[idx];
  next[idx] = {
    ...d,
    onHoliday: true,
    status: 'holiday',
    // 10/02/2026 (Lot N P1) — Auto-trigger : non planifié par le joueur,
    // tolérance client réduite (CA -10 % au-delà de 5 j d'absence).
    holidayPlanned: false,
    holidayStartDay: totalDays,
    holidayEndDay: totalDays + HOLIDAY_BLOCK_LENGTH,
    holidayDaysRemaining: Math.max(0, (d.holidayDaysRemaining || 0) - HOLIDAY_BLOCK_LENGTH),
    holidayDaysTakenYear: Math.round(((d.holidayDaysTakenYear || 0) + HOLIDAY_BLOCK_LENGTH) * 10) / 10,
    // Note : `assignedVehicleId` is NOT cleared. The link survives the
    // holiday so the driver resumes their vehicle on return. Whether the
    // vehicle keeps running depends on the player toggling an interim
    // (vehicle.useInterim flag) — handled in the contract tick.
  };
  return { drivers: next, startedDriverId: d.id, startedDriverName: d.name };
}

/**
 * Eligibility predicate (exported for tests).
 */
export function isDriverHolidayEligible(d) {
  if (!d) return false;
  if (d.onHoliday) return false;
  if (d.status === 'training') return false;
  if (d.status === 'on_break') return false;
  if (d.assignedVehicleId) return false;        // currently actively working
  if ((d.holidayDaysRemaining || 0) < HOLIDAY_TRIGGER_MIN_BALANCE) return false;
  // We don't track "consecutive idle days" precisely — use a simple proxy :
  // if status === 'idle' AND fatigue ≥ 30, they've been around long enough.
  if (d.status !== 'idle') return false;
  if ((d.fatigue || 0) < 10) return false;      // fresh hires can wait
  return true;
}

/* ── Holiday pressure penalty (driver-rights compliance) ───────────────
 * Created 11/06/2026 (soir 9, vague 2 bis) on user suggestion.
 * Rationale : in France, employers MUST grant the legally-due holidays
 * each year. Forcing a driver to hoard their balance is unrealistic and
 * should hurt morale.
 *
 * Trigger : driver has ≥ HOLIDAY_PRESSURE_THRESHOLD days banked AND we
 * are past `HOLIDAY_PRESSURE_MONTH` (June by default). The malus is
 * applied progressively : −1 satisfaction every 7 days, capped at
 * HOLIDAY_PRESSURE_MAX_DROP (10 sat total from this mechanic) so the
 * driver doesn't spiral into immediate burnout.
 *
 * To avoid hitting drivers multiple times per tick, we stamp them with
 * `lastHolidayPressureDay`.
 */
const HOLIDAY_PRESSURE_THRESHOLD = 20;   // 20+ days banked = warning zone
const HOLIDAY_PRESSURE_MONTH = 6;        // June onwards
const HOLIDAY_PRESSURE_INTERVAL_DAYS = 7;
const HOLIDAY_PRESSURE_DROP = 1;         // -1 sat per tick
const HOLIDAY_PRESSURE_MAX_DROP_PER_YEAR = 10;

export function applyHolidayPressurePenalty(drivers, totalDays, month) {
  if (!Array.isArray(drivers)) return { drivers, penalizedDriverIds: [] };
  if ((month || 0) < HOLIDAY_PRESSURE_MONTH) return { drivers, penalizedDriverIds: [] };
  const penalizedDriverIds = [];
  const next = drivers.map(d => {
    if (d.onHoliday) return d;
    if ((d.holidayDaysRemaining || 0) < HOLIDAY_PRESSURE_THRESHOLD) return d;
    const last = d.lastHolidayPressureDay || 0;
    if (totalDays - last < HOLIDAY_PRESSURE_INTERVAL_DAYS) return d;
    // Cap the cumulative annual loss.
    const lossSoFar = d.holidayPressureLossThisYear || 0;
    if (lossSoFar >= HOLIDAY_PRESSURE_MAX_DROP_PER_YEAR) return d;
    penalizedDriverIds.push(d.id);
    return {
      ...d,
      satisfaction: Math.max(0, (d.satisfaction || 0) - HOLIDAY_PRESSURE_DROP),
      lastHolidayPressureDay: totalDays,
      holidayPressureLossThisYear: lossSoFar + HOLIDAY_PRESSURE_DROP,
    };
  });
  return { drivers: next, penalizedDriverIds };
}

/**
 * Notification helper for the first time a driver hits the pressure
 * malus this year (signals to the player they need to grant rest).
 */
export function buildHolidayPressureNotification(driverName, balance, totalDays) {
  return {
    type: 'driver_holiday_pressure',
    title: 'Pression congés non pris',
    message: `${driverName} cumule ${balance} j de congés non pris — sa satisfaction commence à en pâtir. Envoyez-le·la en congés ou activez un intérim.`,
    day: totalDays,
    read: false,
  };
}

/**
 * Reset the per-year pressure counter on Jan 1.
 */
export function resetYearlyPressureCounters(drivers, isYearStart) {
  if (!isYearStart || !Array.isArray(drivers)) return drivers;
  return drivers.map(d => ({
    ...d,
    holidayPressureLossThisYear: 0,
    lastHolidayPressureDay: 0,
    // 13/06/2026 — CET : le plafond de rachat de congés (10 j/an) se
    // réinitialise aussi au 1er janvier.
    holidayDaysBoughtThisYear: 0,
  }));
}

/* ── Year-end negotiated departures (driver-rights compliance) ─────────
 * Created 11/06/2026 (vague 2 ter) on user suggestion.
 * 23/07/2026 (choix user 3a) — REFONTE : plus de départ sec au 1er janvier.
 * Le 1er décembre, chaque chauffeur ayant subi la pénalité annuelle
 * complète (10 sat perdus, congés refusés toute l'année) dépose un
 * PRÉAVIS de 30 jours (pendingDeparture, visible dans la bannière RH)
 * accompagné d'une lettre de décision : accorder 5 j de congés maintenant
 * (il reste, compteur remis à zéro) ou acter le départ négocié
 * (indemnité ~3 mois de coût employeur). Sans réponse, le préavis expire
 * au 1er janvier → départ via driverDepartureTick (indemnité débitée).
 */
const PRESSURE_DEPARTURE_THRESHOLD = 10;     // = the annual cap → "maxed out"
const PRESSURE_DEPARTURE_INDEMNITY_MONTHS = 3;
const PRESSURE_NOTICE_DAYS = 30;

/**
 * 1er décembre — stamp le préavis sur chaque chauffeur au cap de pression.
 * Idempotent par année (`pressureNoticeYear`). Retourne la liste des
 * notices pour pousser lettres + notifications.
 */
export function applyPressureDepartureNotices(drivers, totalDays = 0, yearTag = 0) {
  if (!Array.isArray(drivers)) return { drivers, notices: [] };
  const notices = [];
  const next = drivers.map(d => {
    if ((d.holidayPressureLossThisYear || 0) < PRESSURE_DEPARTURE_THRESHOLD) return d;
    if (d.pendingDeparture) return d;                    // déjà en préavis (autre motif)
    if (d.pressureNoticeYear === yearTag) return d;      // déjà notifié cette année
    const monthlyCost = d.employerCost || d.salaryGross || 2800;
    const indemnity = Math.round(monthlyCost * PRESSURE_DEPARTURE_INDEMNITY_MONTHS);
    notices.push({
      id: d.id,
      dataId: d.dataId,
      name: d.name,
      indemnity,
      balance: d.holidayDaysRemaining || 0,
    });
    return {
      ...d,
      pressureNoticeYear: yearTag,
      pendingDeparture: {
        noticeUntil: totalDays + PRESSURE_NOTICE_DAYS,
        triggeredOn: totalDays,
        reason: 'holiday_pressure',
        indemnity,
      },
    };
  });
  return { drivers: next, notices };
}

/** Lettre de préavis « congés non accordés » (email decision). */
export function buildPressureDepartureLetter(notice) {
  return {
    kind: 'decision',
    senderType: 'driver',
    senderName: notice.name,
    senderRole: 'Chauffeur',
    tone: 'formal',
    subject: `Préavis — congés non accordés (${notice.name})`,
    body:
      `Madame, Monsieur,\n\nCela fait un an que mes demandes de congés restent lettre morte `
      + `(${notice.balance} jours non pris). Le droit du travail est clair et ma patience a des limites : `
      + `je dépose un **préavis de ${PRESSURE_NOTICE_DAYS} jours**.\n\n`
      + `Deux issues possibles :\n`
      + `• Vous m'accordez **5 jours de congés immédiatement** (un intérimaire couvrira ma mission si besoin) — je retire mon préavis.\n`
      + `• Nous actons une **rupture négociée** avec l'indemnité conventionnelle de ${notice.indemnity.toLocaleString('fr-FR')} €.\n\n`
      + `Sans réponse de votre part, mon départ sera effectif à l'expiration du préavis (1er janvier).\n\n${notice.name}`,
    driverId: notice.id,
    ttlDays: PRESSURE_NOTICE_DAYS + 5,
    choices: [
      {
        id: 'grant',
        label: 'Accorder 5 j de congés maintenant (annule le préavis)',
        effects: [{ kind: 'driverGrantPressureHoliday' }],
      },
      {
        id: 'accept',
        label: `Acter le départ négocié (indemnité ${notice.indemnity.toLocaleString('fr-FR')} €)`,
        effects: [{ kind: 'driverAcceptDeparture' }],
      },
    ],
    // Pas de defaultChoiceId : si le joueur ignore la lettre, le préavis
    // expire naturellement via driverDepartureTick (départ + indemnité).
  };
}

export function buildPressureNoticeNotification({ name, indemnity }, totalDays) {
  return {
    type: 'driver_pressure_notice',
    title: `Préavis déposé — ${name}`,
    message: `${name} dépose un préavis de ${PRESSURE_NOTICE_DAYS} j pour congés non accordés. Accordez-lui 5 j de congés (messagerie) ou son départ sera acté au 1er janvier (indemnité −${indemnity.toLocaleString('fr-FR')} €).`,
    day: totalDays,
    read: false,
  };
}

/* ── Nadia Cheikh mid-cap alert (vague 2 quater) ─────────────────────────
 * When the player has hired Nadia Cheikh (Responsable RH) and a driver
 * reaches HALF of the annual pressure cap (5 sat lost from holidays
 * denied), she emails the player with a concrete action item. Surfaces
 * her value as a "vigie sociale" before the year-end departure hits.
 *
 * The signal is per-driver, per-year : we stamp the driver with
 * `holidayPressureNadiaAlertSent` so we don't spam the inbox after the
 * first email of the year.
 */
const NADIA_ALERT_THRESHOLD = 5;     // mid-way to the 10-sat departure cap

export function buildNadiaPressureAlertMessage(driverName, balance, lossSoFar) {
  return {
    senderType: 'support',
    senderName: 'Nadia Cheikh (Resp. RH)',
    senderRole: 'role-nadia-cheikh',
    tone: 'advice',
    kind: 'warning',
    subject: `Vigilance congés — ${driverName}`,
    body:
      `Patron,\n\n${driverName} cumule ${balance} jours de congés non pris et a perdu ${lossSoFar} points de satisfaction cette année.\n\n`
      + `À ce rythme, il/elle pourrait quitter l'entreprise au 31 décembre (départ négocié coûteux). Je vous suggère deux options :\n\n`
      + `• L'envoyer en congés (5 jours) dès maintenant via la page RH.\n`
      + `• Activer un intérimaire sur son véhicule pour permettre la prise de congés sans interrompre le contrat (tarif fixe par tier).\n\n`
      + `Mieux vaut anticiper.\n\n— Nadia Cheikh`,
    ttlDays: 30,
  };
}

/**
 * Return the list of drivers that just crossed the mid-cap pressure
 * threshold AND haven't been flagged for a Nadia alert this year yet.
 * Also returns the updated drivers array (with the flag stamped).
 *
 * Caller decides whether to actually push the message (depends on
 * whether Nadia is on payroll).
 */
export function pickNadiaPressureAlerts(drivers) {
  if (!Array.isArray(drivers)) return { drivers, alerts: [] };
  const alerts = [];
  const next = drivers.map(d => {
    const loss = d.holidayPressureLossThisYear || 0;
    if (loss < NADIA_ALERT_THRESHOLD) return d;
    if (d.holidayPressureNadiaAlertSent) return d;
    alerts.push({
      id: d.id,
      name: d.name,
      balance: d.holidayDaysRemaining || 0,
      lossSoFar: loss,
    });
    return { ...d, holidayPressureNadiaAlertSent: true };
  });
  return { drivers: next, alerts };
}

/**
 * Reset the Nadia alert flag at year start (same time as the loss
 * counter) so each year gets its own warning cycle.
 */
export function resetNadiaPressureAlertFlags(drivers, isYearStart) {
  if (!isYearStart || !Array.isArray(drivers)) return drivers;
  return drivers.map(d => ({ ...d, holidayPressureNadiaAlertSent: false }));
}

/**
 * Build an in-game notification for the start of a holiday.
 */
export function buildHolidayStartNotification(driverName, totalDays, daysRemaining) {
  return {
    type: 'driver_holiday_start',
    title: 'Départ en congés',
    message: `${driverName} part en congés pour ${HOLIDAY_BLOCK_LENGTH} jours (solde restant : ${daysRemaining} j).`,
    day: totalDays,
    read: false,
  };
}

/**
 * Build an in-game notification for the end of a holiday.
 */
export function buildHolidayReturnNotification(driverName, totalDays) {
  return {
    type: 'driver_holiday_end',
    title: 'Retour de congés',
    message: `${driverName} est de retour, fraîchement reposé·e.`,
    day: totalDays,
    read: false,
  };
}

/* Re-exported constants for tests and UI. */
export const HOLIDAY_CONSTANTS = {
  FULL_YEAR: HOLIDAY_FULL_YEAR,
  MONTHLY_ACCRUAL: HOLIDAY_MONTHLY_ACCRUAL,
  CARRYOVER_CAP: HOLIDAY_CARRYOVER_CAP,
  BLOCK_LENGTH: HOLIDAY_BLOCK_LENGTH,
  TRIGGER_IDLE_DAYS: HOLIDAY_TRIGGER_IDLE_DAYS,
  TRIGGER_MIN_BALANCE: HOLIDAY_TRIGGER_MIN_BALANCE,
  PRESSURE_THRESHOLD: HOLIDAY_PRESSURE_THRESHOLD,
  PRESSURE_MONTH: HOLIDAY_PRESSURE_MONTH,
  PRESSURE_INTERVAL_DAYS: HOLIDAY_PRESSURE_INTERVAL_DAYS,
  PRESSURE_DROP: HOLIDAY_PRESSURE_DROP,
  PRESSURE_MAX_DROP_PER_YEAR: HOLIDAY_PRESSURE_MAX_DROP_PER_YEAR,
  DEPARTURE_THRESHOLD: PRESSURE_DEPARTURE_THRESHOLD,
  DEPARTURE_INDEMNITY_MONTHS: PRESSURE_DEPARTURE_INDEMNITY_MONTHS,
  PRESSURE_NOTICE_DAYS,
  NADIA_ALERT_THRESHOLD,
};

/* ── Interim driver tariff (fixed per vehicle tier) ────────────────────
 * Created in response to user feedback : when titular drivers are on
 * holiday, the player should be able to hire a temporary interim driver
 * to keep the contract running — without creating a permanent HR record.
 * The cost is a fixed daily charge logged in dailyExpenses.
 */
export const INTERIM_DAILY_TARIFF = {
  1: 120,
  2: 180,
  3: 250,
  4: 320,
  5: 400,
};

export function getInterimDailyCost(vehicleTier) {
  return INTERIM_DAILY_TARIFF[vehicleTier] || INTERIM_DAILY_TARIFF[1];
}
